import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import ActivityCard from '../components/ActivityCard';
import CustomAmountCard from '../components/CustomAmountCard';
import CartSummary from '../components/CartSummary';
import { useCart } from '../context/CartContext';
import { fetchActivities } from '../services/api';

const HomePage = ({ onNavigateToCheckout, onSignOut }) => {
  const { addItem, removeItem } = useCart();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchActivities();
      setActivities(data);
    } catch (err) {
      console.error('Failed to load activities:', err);
      setError('Failed to load activities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Sort activities: available first, then unavailable
  const sortedActivities = [...activities].sort((a, b) => {
    if (a.availableQuantity === 0 && b.availableQuantity > 0) return 1;
    if (a.availableQuantity > 0 && b.availableQuantity === 0) return -1;
    return 0;
  });

  const handleAddToCart = (activity) => {
    // Custom amounts don't have quantity tracking
    if (activity.id.startsWith('custom-')) {
      addItem(activity);
      return;
    }

    if (activity.availableQuantity === 0) return;

    // Update available quantity
    setActivities(prevActivities =>
      prevActivities.map(a =>
        a.id === activity.id
          ? { ...a, availableQuantity: a.availableQuantity - 1 }
          : a
      )
    );

    // Add to cart with unique cart item ID
    addItem({
      ...activity,
      cartItemId: `${activity.id}-${Date.now()}-${Math.random()}`, // Unique ID for each cart item
      amount: activity.price,
    });
  };

  const handleRemoveFromCart = (cartItemId, activityId) => {
    // Restore available quantity for regular activities (not custom amounts)
    if (!activityId.startsWith('custom-')) {
      setActivities(prevActivities =>
        prevActivities.map(a =>
          a.id === activityId
            ? { ...a, availableQuantity: a.availableQuantity + 1 }
            : a
        )
      );
    }

    // Remove from cart using the unique cart item ID
    removeItem(cartItemId);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Sign Out Button */}
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={onSignOut}
          className="bg-white text-gray-700 px-4 py-2 rounded-md shadow-md hover:bg-gray-100 transition-colors flex items-center gap-2"
        >
          <span>Sign Out</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </button>
      </div>

      <Header
        coupleName="Ruth & Benjamin"
        destination="Australia"
        description="Help us create unforgettable memories on our honeymoon adventure"
        imageUrl="/images/header-image.png"
      />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">
          Choose a Memory
        </h2>
        <p className="text-gray-600 text-center mb-8">
          and we'll send you photos of the adventure...
        </p>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading activities...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-800 mb-4">{error}</p>
            <button
              onClick={loadActivities}
              className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Activities Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Available activities */}
            {sortedActivities
              .filter(activity => activity.availableQuantity > 0)
              .map((activity) => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  onAddToCart={handleAddToCart}
                />
              ))}
            
            {/* Custom amount card */}
            <CustomAmountCard onAddToCart={handleAddToCart} />
            
            {/* Unavailable activities */}
            {sortedActivities
              .filter(activity => activity.availableQuantity === 0)
              .map((activity) => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  onAddToCart={handleAddToCart}
                />
              ))}
          </div>
        )}
      </div>

      <CartSummary 
        onCheckout={onNavigateToCheckout}
        onRemoveItem={handleRemoveFromCart}
      />
    </div>
  );
};

export default HomePage;
